import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { useAuth } from '../providers'
import { usePlanFeatures } from './use-plan-features'

/** One monthly allowance: "N of `limit` left". */
export interface AllowanceCount {
  limit: number
  used: number
  remaining: number
}

/** GET /v1/billing/allowances: Free's capped allowances, each null on a plan without that cap. */
export interface PlanAllowances {
  meetingBriefs: AllowanceCount | null
  actionUnlocks: AllowanceCount | null
  /** When the counts reset, or null on a plan without a cap. */
  periodEndsAt: string | null
  /** When Free's one-time grace hour was granted, or null while it's still owed. */
  freeGraceHourGrantedAt: string | null
}

const PLAN_ALLOWANCES_QUERY_KEY = ['plan-allowances'] as const

/**
 * The server's count of what's left this billing period. Only fetched on a plan
 * with a cap; the server enforces the caps either way, so this is for display.
 */
export function usePlanAllowances() {
  const { getAuthToken } = useAuth()
  const { monthlyMeetingBriefs, monthlyActionUnlocks } = usePlanFeatures()
  return useQuery({
    // Keyed by the caps too, so a plan change never serves the old plan's counts: on an
    // upgrade the query switches off, and Free's spent briefs must not keep locking the brief.
    queryKey: [...PLAN_ALLOWANCES_QUERY_KEY, monthlyMeetingBriefs, monthlyActionUnlocks],
    enabled: monthlyMeetingBriefs !== null || monthlyActionUnlocks !== null,
    staleTime: 60_000,
    queryFn: async (): Promise<PlanAllowances> => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/billing/allowances'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        throw new Error(`Failed to load plan allowances (${response.status})`)
      }
      return ((await response.json()) as { data: PlanAllowances }).data
    },
  })
}

/** Refetches the allowances after something spent one. */
export function useInvalidatePlanAllowances(): () => Promise<void> {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: PLAN_ALLOWANCES_QUERY_KEY })
}
